import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { AiOutlineFileAdd } from "react-icons/ai";
import { BsPencilFill, BsFileEarmarkCode } from "react-icons/bs";
import { LuFilter, LuX } from 'react-icons/lu';
import { GrAttachment, GrInbox } from "react-icons/gr";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { IoSettingsOutline } from "react-icons/io5";
import axios from 'axios';
import Header from '../../../header/header.js';
import Navvbar from '../../../header/Navvbar.js';
import './CasesCancelledReportView.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

const PageWrapper = styled.div`
  font-family: Arial, sans-serif;
  padding: 20px;
`;

const Headers = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #004aad;
  margin: 0;
`;

const ParamsButton = styled.button`
  background-color: #004aad;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: bold;
  &:hover {
    background-color: #003080;
  }
`;

const SummarySection = styled.div`
  margin-bottom: 30px;
`;

const SummaryTitle = styled.h3`
  color: #004aad;
  margin-bottom: 10px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #ccc;
  color: black;
  font-weight: bold;
`;

const SummaryDataRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  font-style: italic;
  background-color: transparent;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  th, td {
    padding: 10px;
    text-align: left;
  }
  th {
    background-color: #eee;
  }
  tbody tr:hover {
    background-color: #f2f2f2;
  }
`;

const BlueLink = styled.a`
  color: #004aad;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-family: Arial, sans-serif;
  margin-bottom: 20px;
`;

const TitleContainer = styled.div`
  margin-bottom: 10px;
`;

const FormTitle = styled.h3`
  color: #004aad;
  margin: 0;
`;

const Container = styled.div`
  border-top: 2px solid #004aad;
  padding: 20px;
  width: 700px;
  background-color: #f7f7f7;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  font-weight: bold;
  display: block;
`;

const Required = styled.span`
  color: red;
  margin-left: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid #ccc;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid #ccc;
`;

const RunButton = styled.button`
  background-color: #004aad;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  &:hover {
    background-color: #003080;
  }
`;

const CasesCancelledReportView = () => {
  const [cases, setCases] = useState([]);
  const [showParams, setShowParams] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const now = new Date();
  const defaultStartDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const defaultEndDate = now.toISOString().split('T')[0];

  const { startDate: initialStartDate, endDate: initialEndDate, format } = location.state || {
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    format: 'On Screen'
  };

  const [filters, setFilters] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate,
    cancellationReason: ''
  });

  // Format invoice number
  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, '0') : 'N/A';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  // Fetch invoice details for a specific invoice number
  const fetchInvoiceDetails = async (invoiceNumber) => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${invoiceNumber}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      return null;
    }
  };

  // Fetch all canceled cases for the logged-in lab
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get userLabId from localStorage
        const userLabId = localStorage.getItem('userLabId') ||
                          localStorage.getItem('labId') ||
                          localStorage.getItem('userId');

        if (!userLabId) {
          setError('User Lab ID not found. Please login again.');
          setLoading(false);
          return;
        }

        console.log('🔍 Fetching canceled cases for lab:', userLabId);

        // Call the new dedicated endpoint
        const response = await axios.get(`${API_URL}/api/cases/canceled-cases`, {
          headers: { 'Content-Type': 'application/json' },
          params: { userLabId },
          withCredentials: true,
        });

        console.log('🔍 Full response:', response.data);

        // Extract cases array safely
        const allCases = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];

        // Frontend safety filter — only canceled cases
        const canceledCases = allCases.filter(
          (c) => c.canceled === true ||
                 c.status?.toLowerCase() === 'cancelled' ||
                 c.status?.toLowerCase() === 'canceled'
        );

        console.log('✅ Canceled cases count:', canceledCases.length);

        setCases(canceledCases);
      } catch (error) {
        console.error('❌ Error fetching canceled cases:', error);
        setError(
          error.response?.data?.message ||
            'Failed to load canceled cases. Please try again later.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Apply filters to cases
  const applyFilters = (casesToFilter) => {
    return casesToFilter.filter((caseItem) => {
      const cancelDate = new Date(caseItem.cancellationDate);
      if (isNaN(cancelDate.getTime())) return false;

      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);

      if (cancelDate < start || cancelDate > end) return false;

      if (filters.cancellationReason && caseItem.cancellationReason !== filters.cancellationReason) return false;

      return true;
    });
  };

  // Memoized filtered cases
  const filteredCases = useMemo(() => {
    return applyFilters(cases);
  }, [cases, filters]);

  // Calculate summary data
  const calculateSummary = useMemo(() => {
    const summary = {};
    filteredCases.forEach(caseItem => {
      const reason = caseItem.cancellationReason || 'Unspecified Reason';
      summary[reason] = (summary[reason] || 0) + 1;
    });
    return summary;
  }, [filteredCases]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowParams(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading canceled cases...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p style={{ color: 'red' }}>{error}</p>
      </div>
    );
  }

  return (
    <>
      <Header />
      <Navvbar />
      <div
        className="ccr-outer"
        style={{ padding: '20px', fontFamily: 'Arial, sans-serif', minHeight: '100vh', width: "95%", margin: "0 auto" }}
      >
        <PageWrapper className="ccr-page">
          <Headers className="ccr-header">
            <Title className="ccr-title">
              Cases Canceled {new Date(filters.startDate).toLocaleDateString('en-GB')} to {new Date(filters.endDate).toLocaleDateString('en-GB')}
            </Title>
            <ParamsButton className="ccr-params-btn" onClick={() => setShowParams(!showParams)}>
              {showParams ? "Hide Parameters" : "Parameters"}
            </ParamsButton>
          </Headers>

          {showParams && (
            <Wrapper className="ccr-form-wrapper">
              <TitleContainer>
                <FormTitle>⚙️ Parameters</FormTitle>
              </TitleContainer>
              <Container className="ccr-form-container">
                <FormGroup>
                  <Label>
                    Start Date<Required>*</Required>
                  </Label>
                  <Input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    End Date<Required>*</Required>
                  </Label>
                  <Input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Cancellation Reason</Label>
                  <Select name="cancellationReason" value={filters.cancellationReason} onChange={handleFilterChange}>
                    <option value="">All Reasons</option>
                    {Array.from(new Set(cases.map(caseItem => caseItem.cancellationReason || 'Unspecified Reason'))).map(reason => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </Select>
                </FormGroup>

                <RunButton className="ccr-run-btn" onClick={handleApplyFilters}>🔍 Run</RunButton>
              </Container>
            </Wrapper>
          )}

          <SummarySection className="ccr-summary">
            <SummaryTitle>Summary</SummaryTitle>
            <SummaryRow className="ccr-summary-row">
              <p>Cancellation Reason</p>
              <p>Count</p>
            </SummaryRow>
            {Object.entries(calculateSummary).map(([reason, count]) => (
              <SummaryDataRow className="ccr-summary-data-row" key={reason}>
                <p>{reason}</p>
                <p>{count}</p>
              </SummaryDataRow>
            ))}
          </SummarySection>

          <TableWrapper className="ccr-table-wrapper">
            <StyledTable className="ccr-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Cancellation Reason</th>
                  <th>Case ID</th>
                  <th>Value</th>
                  <th>By Lab #</th>
                  <th>By Lab $</th>
                  <th>By Account #</th>
                  <th>By Account $</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.length > 0 ? (
                  filteredCases.map(caseItem => {
                    const invoiceData = caseItem.invoiceNumber ? {
                      invoiceNumber: caseItem.invoiceNumber,
                      invoiceTotal: caseItem.invoiceTotal,
                      caseId: caseItem.caseNumber,
                    } : null;

                    return (
                      <tr key={caseItem._id} style={{ borderBottom: "1px solid #ddd" }}>
                        <td>
                          <BlueLink href={`/case/${caseItem.caseNumber}`}>
                            {caseItem.account_name || caseItem.account_holder || "N/A"}
                          </BlueLink>
                        </td>
                        <td><i>{caseItem.cancellationReason || 'Unspecified Reason'}</i></td>
                        <td><BlueLink href={`/case/${caseItem.caseNumber}`}>{caseItem.caseNumber}</BlueLink></td>
                        <td>£{caseItem.totalPrice?.toFixed(2) || '0.00'}</td>
                        <td>1</td>
                        <td>£{caseItem.totalPrice?.toFixed(2) || '0.00'}</td>
                        <td></td>
                        <td></td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                      No canceled cases match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </StyledTable>
          </TableWrapper>
        </PageWrapper>
      </div>
    </>
  );
};

export default CasesCancelledReportView;